import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, Target as TargetIcon, Plus as PlusIcon } from "lucide-react";
import { toast } from "sonner";

function Modal({ open, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-black w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function monthsBetween(deadline) {
  if (!deadline) return null;
  const now = new Date();
  const d = new Date(deadline);
  const months = (d.getFullYear() - now.getFullYear()) * 12 + (d.getMonth() - now.getMonth());
  return Math.max(1, months);
}

export default function Goals() {
  const { user } = useAuth();
  const currency = user?.currency || "USD";
  const [goals, setGoals] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openContrib, setOpenContrib] = useState(null);
  const [form, setForm] = useState({ name: "", target_amount: "", deadline: "", initial_amount: "" });
  const [contrib, setContrib] = useState({ amount: "", note: "" });

  const load = async () => {
    const { data } = await api.get("/goals");
    setGoals(data);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/goals", {
        name: form.name,
        target_amount: parseFloat(form.target_amount),
        deadline: form.deadline || null,
        initial_amount: parseFloat(form.initial_amount) || 0,
      });
      toast.success("Goal created");
      setOpenCreate(false);
      setForm({ name: "", target_amount: "", deadline: "", initial_amount: "" });
      load();
    } catch (err) {
      toast.error("Failed to create goal");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this goal?")) return;
    await api.delete(`/goals/${id}`);
    toast.success("Goal deleted");
    load();
  };

  const addContrib = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/goals/${openContrib.id}/contributions`, {
        amount: parseFloat(contrib.amount),
        note: contrib.note,
      });
      toast.success("Contribution added");
      setOpenContrib(null);
      setContrib({ amount: "", note: "" });
      load();
    } catch (err) {
      toast.error("Failed to add contribution");
    }
  };

  return (
    <div className="space-y-6" data-testid="goals-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="overline">SAVINGS PLAN</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Goals</h1>
          <p className="font-mono text-sm text-[var(--text-secondary)] mt-2">Set targets. Save monthly. Watch the bar fill.</p>
        </div>
        <button
          data-testid="add-goal-button"
          onClick={() => setOpenCreate(true)}
          className="bg-black text-white px-4 py-2 font-display font-bold text-sm hover:bg-[var(--action-blue)] transition-colors flex items-center gap-2 w-fit"
        >
          <Plus size={14} /> NEW GOAL
        </button>
      </div>

      {goals.length === 0 ? (
        <div className="bg-white border border-black/15 p-10 text-center" data-testid="empty-goals">
          <TargetIcon size={32} className="mx-auto mb-3" />
          <div className="font-display font-bold text-xl">No goals yet</div>
          <div className="font-mono text-sm text-[var(--text-secondary)] mt-1">Start with something concrete — a trip, an emergency fund, a new laptop.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {goals.map((g) => {
            const pct = Math.min(100, Math.round((g.current_amount / g.target_amount) * 100));
            const remaining = Math.max(0, g.target_amount - g.current_amount);
            const monthsLeft = monthsBetween(g.deadline);
            const required = monthsLeft ? Math.ceil(remaining / monthsLeft) : null;
            const complete = g.current_amount >= g.target_amount;
            return (
              <div key={g.id} data-testid={`goal-card-${g.id}`}
                className={`bg-white border-2 p-6 ${complete ? "border-[var(--success-green)]" : "border-black/15"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="overline">{g.deadline ? `BY ${g.deadline}` : "OPEN-ENDED"}</div>
                    <h3 className="font-display font-black text-2xl tracking-tighter mt-1">{g.name}</h3>
                  </div>
                  <button data-testid={`delete-goal-${g.id}`} onClick={() => del(g.id)} className="p-2 hover:bg-[var(--alert-red)] hover:text-white">
                    <Trash2 size={14} />
                  </button>
                </div>

                <div className="mt-4 font-mono">
                  <div className="flex justify-between items-baseline">
                    <span className="text-2xl font-bold">{fmtMoney(g.current_amount, currency)}</span>
                    <span className="text-sm text-[var(--text-secondary)]">/ {fmtMoney(g.target_amount, currency)}</span>
                  </div>
                  <div className="h-2 bg-black/10 mt-2">
                    <div
                      className={`h-full ${complete ? "bg-[var(--success-green)]" : "bg-black"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="text-xs text-[var(--text-secondary)] mt-1">{pct}% saved</div>
                </div>

                {!complete && required && (
                  <div className="mt-3 font-mono text-sm border-l-2 border-[var(--action-blue)] pl-3">
                    Save <span className="font-bold">{fmtMoney(required, currency)}/mo</span> for {monthsLeft} months to hit target.
                  </div>
                )}
                {complete && (
                  <div className="mt-3 font-mono text-sm text-[var(--success-green)] font-bold">✓ GOAL REACHED</div>
                )}

                <button
                  data-testid={`add-contrib-${g.id}`}
                  onClick={() => setOpenContrib(g)}
                  className="mt-4 w-full border-2 border-black py-2 font-display font-bold text-sm hover:bg-black hover:text-white transition-colors flex items-center justify-center gap-2"
                >
                  <PlusIcon size={14} /> ADD CONTRIBUTION
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Create goal modal */}
      <Modal open={openCreate} onClose={() => setOpenCreate(false)}>
        <form onSubmit={create} data-testid="goal-form">
          <div className="overline mb-2">NEW GOAL</div>
          <h3 className="font-display font-black text-2xl tracking-tighter mb-6">What are you saving for?</h3>

          <label className="block mb-4">
            <div className="overline mb-1">NAME</div>
            <input data-testid="goal-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Iceland trip"
              className="w-full bg-transparent border-b-2 border-black/20 focus:border-black outline-none py-2 text-lg font-mono" />
          </label>
          <label className="block mb-4">
            <div className="overline mb-1">TARGET AMOUNT ({currency})</div>
            <input data-testid="goal-target" required type="number" min="1" step="0.01" value={form.target_amount}
              onChange={(e) => setForm({ ...form, target_amount: e.target.value })}
              className="w-full bg-transparent border-b-2 border-black/20 focus:border-black outline-none py-2 text-lg font-mono" />
          </label>
          <label className="block mb-4">
            <div className="overline mb-1">DEADLINE (OPTIONAL)</div>
            <input data-testid="goal-deadline" type="date" value={form.deadline}
              onChange={(e) => setForm({ ...form, deadline: e.target.value })}
              className="w-full border-2 border-black bg-white py-2 px-2 font-mono" />
          </label>
          <label className="block mb-6">
            <div className="overline mb-1">ALREADY SAVED (OPTIONAL)</div>
            <input data-testid="goal-initial" type="number" min="0" step="0.01" value={form.initial_amount}
              onChange={(e) => setForm({ ...form, initial_amount: e.target.value })}
              className="w-full bg-transparent border-b-2 border-black/20 focus:border-black outline-none py-2 text-lg font-mono" />
          </label>
          <div className="flex gap-2">
            <button type="button" onClick={() => setOpenCreate(false)} className="flex-1 border-2 border-black py-2 font-display font-bold hover:bg-black hover:text-white">CANCEL</button>
            <button data-testid="goal-submit" type="submit" className="flex-1 bg-black text-white py-2 font-display font-bold hover:bg-[var(--action-blue)]">CREATE</button>
          </div>
        </form>
      </Modal>

      {/* Contribution modal */}
      <Modal open={!!openContrib} onClose={() => setOpenContrib(null)}>
        <form onSubmit={addContrib} data-testid="contrib-form">
          <div className="overline mb-2">DEPOSIT INTO</div>
          <h3 className="font-display font-black text-2xl tracking-tighter mb-6">{openContrib?.name}</h3>
          <label className="block mb-4">
            <div className="overline mb-1">AMOUNT ({currency})</div>
            <input data-testid="contrib-amount" required type="number" min="0.01" step="0.01" value={contrib.amount}
              onChange={(e) => setContrib({ ...contrib, amount: e.target.value })}
              className="w-full bg-transparent border-b-2 border-black/20 focus:border-black outline-none py-2 text-lg font-mono" />
          </label>
          <label className="block mb-6">
            <div className="overline mb-1">NOTE (OPTIONAL)</div>
            <input data-testid="contrib-note" value={contrib.note}
              onChange={(e) => setContrib({ ...contrib, note: e.target.value })}
              className="w-full bg-transparent border-b-2 border-black/20 focus:border-black outline-none py-2 text-lg font-mono" />
          </label>
          <div className="flex gap-2">
            <button type="button" onClick={() => setOpenContrib(null)} className="flex-1 border-2 border-black py-2 font-display font-bold hover:bg-black hover:text-white">CANCEL</button>
            <button data-testid="contrib-submit" type="submit" className="flex-1 bg-black text-white py-2 font-display font-bold hover:bg-[var(--action-blue)]">DEPOSIT</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
